import { useLocation } from "react-router-dom";
import { getAppointmentActionType } from "../../../core/store/appointmentSlice";
import {
  getBookableConfigPractices,
  getBookableTherapyDepartments,
  getCurrentPracticeAppointmentRequestFlags,
  getCurrentPracticeCustomAppointmentOptions,
} from "../../../core/store/chainSlice";
import { State, useMainStore } from "../../../core/store/mainStore";
import { useWidgetConfig } from "../../../widget/WidgetContext";
import type { AppointmentBookingStepNames } from "../../navigation/utils/useHandleAppointmentBookingNavigation";
import { showsDetailOptions } from "./detailOptions";

/** Practice pinned by the entry link. All three alias spellings are in circulation. */
export const getPinnedPracticeId = (search: string) => {
  const params = new URLSearchParams(search);
  return (
    params.get("pid") ??
    params.get("practiceId") ??
    params.get("practiceid") ??
    undefined
  );
};

/**
 * PF-5480: the practice selection step is skipped when the entry link (or the widget's `data-practice-id`)
 * pins a bookable practice, or when the chain has exactly one bookable practice. An invalid pin — typo,
 * meanwhile disabled practice, pin from another chain — deliberately falls back to the normal rule instead
 * of booking against a practice the patient cannot book.
 */
export const usePracticeSelectionSkip = () => {
  const { practiceId: widgetPracticeId } = useWidgetConfig();
  const { search } = useLocation();
  const bookablePractices = useMainStore(getBookableConfigPractices);

  const pinnedId = widgetPracticeId || getPinnedPracticeId(search);
  const pinnedPractice = bookablePractices.find(
    (practice) => practice.practiceId === pinnedId,
  );
  const onlyPractice =
    bookablePractices.length === 1 ? bookablePractices[0] : undefined;
  const practice = pinnedPractice ?? onlyPractice;

  return {
    isSkipped: !!practice,
    practiceId: practice?.practiceId,
    bookablePractices,
  };
};

// One predicate for every reader, so a step cannot exist in one surface and be missing in another.
export const getTherapyDepartmentSkip = (state: State) => {
  const departments = getBookableTherapyDepartments(state);
  return {
    isSkipped: departments.length <= 1,
    departments,
  };
};

export const useTherapyDepartmentSkip = () =>
  useMainStore(getTherapyDepartmentSkip);

// Reads the practice from the store, so callers must set it before calling.
export const resolveStepAfterPracticeSelection = (): "type" | "department" => {
  const state = useMainStore.getState();
  const { isSkipped } = getTherapyDepartmentSkip(state);
  if (!isSkipped) return "department";
  // Clearing matters: the store is persisted, so an earlier booking's department would travel along.
  state.setAppointmentTherapyDepartment(undefined);
  return "type";
};

/** Screens that sit on another screen's step instead of owning one. */
export const SCREENS_ON_SHARED_STEP: Partial<
  Record<AppointmentBookingStepNames, AppointmentBookingStepNames>
> = {
  recipe: "recipeInfo",
  noRecipe: "recipeInfo",
  selfPayerDuration: "recipeInfo",
  selfPayerAdditionalTreatments: "recipeInfo",
};

/**
 * The screens this flow actually walks through, in order. Single source for the progress bar and the
 * desktop sidebar, so neither can drift from the other or from the navigation.
 */
export const getAppointmentFlowScreens = (
  state: State,
  skipPracticeSelection: boolean,
): AppointmentBookingStepNames[] => {
  const entry: AppointmentBookingStepNames[] = skipPracticeSelection
    ? []
    : ["practice"];
  const departmentStep: AppointmentBookingStepNames[] =
    getTherapyDepartmentSkip(state).isSkipped ? [] : ["department"];
  const start = [...entry, ...departmentStep];

  switch (getAppointmentActionType(state)) {
    case "cancellation":
      return [
        ...start,
        "type",
        "phoneNumber",
        "cancellationAppointmentDateTime",
        "cancellationAppointmentContact",
        "success",
      ];
    case "rescheduling":
      return [
        ...start,
        "type",
        "phoneNumber",
        "modificationAppointmentDateTime",
        "modificationAppointmentTimeSlots",
        "modificationAppointmentContact",
        "success",
      ];
    default:
      return [
        ...start,
        "type",
        "recipeInfo",
        "timeSlot",
        ...(showsDetailOptions(
          getCurrentPracticeAppointmentRequestFlags(state),
          getCurrentPracticeCustomAppointmentOptions(state),
        )
          ? (["preferences"] as AppointmentBookingStepNames[])
          : []),
        "contact",
        "success",
      ];
  }
};

/** `null` means the screen is not a flow step (dead ends, orphaned routes) — then no progress bar. */
export const getStepProgress = (
  flow: AppointmentBookingStepNames[],
  screen: AppointmentBookingStepNames,
) => {
  const step = flow.indexOf(SCREENS_ON_SHARED_STEP[screen] ?? screen);
  return step === -1 ? null : { step, totalSteps: flow.length - 1 };
};
